<template>
  <AppDialog
      v-model="dialog"
      title="Merge duplicate whales"
      max-width="900px"
      scrollable
      @close="dialog = false"
  >
    <v-card-text>
      <div v-if="loading" class="d-flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div
          v-else-if="candidates.length < 2"
          class="text-medium-emphasis text-body-2 py-8 text-center"
      >
        Need at least two active records sharing this whale ID to merge.
      </div>

      <template v-else>
        <p class="text-body-2 text-medium-emphasis mb-4">
          The survivor is kept; the duplicate is archived (renamed so it is clearly the
          merged copy). Its sightings, photos and maternal links move to the survivor.
          Choose the winning value for each differing field.
        </p>

        <v-row dense class="mb-2">
          <v-col cols="12" sm="6">
            <v-select
                v-model="survivorId"
                :items="candidateItems"
                label="Survivor (kept)"
                density="compact"
                hide-details
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
                v-model="duplicateId"
                :items="candidateItems"
                label="Duplicate (archived)"
                density="compact"
                hide-details
            />
          </v-col>
        </v-row>

        <v-alert
            v-if="survivorId === duplicateId"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
        >
          Pick two different records to merge.
        </v-alert>

        <v-table v-else density="compact" class="merge-table">
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">Result</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in fieldRows" :key="row.key">
              <td class="merge-field-label">{{ row.label }}</td>
              <td>
                <template v-if="row.conflict">
                  <v-radio-group
                      v-model="choices[row.key]"
                      inline
                      hide-details
                      density="compact"
                      class="merge-choices"
                  >
                    <v-radio :label="displayValue(row.survivorValue)" value="survivor" />
                    <v-radio :label="displayValue(row.duplicateValue)" value="duplicate" />
                    <v-radio label="Custom" value="custom" />
                  </v-radio-group>
                  <v-text-field
                      v-if="choices[row.key] === 'custom'"
                      v-model="customValues[row.key]"
                      density="compact"
                      hide-details
                      placeholder="Enter a value"
                      class="mt-1"
                  />
                </template>
                <span v-else class="text-medium-emphasis">{{ displayValue(row.survivorValue) }}</span>
              </td>
            </tr>
          </tbody>
        </v-table>

        <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
        >
          {{ error }}
        </v-alert>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn @click="dialog = false">
        Cancel
      </v-btn>
      <v-btn
          color="primary"
          :loading="merging"
          :disabled="!canMerge"
          prepend-icon="mdi-call-merge"
          @click="doMerge"
      >
        Merge
      </v-btn>
    </v-card-actions>
  </AppDialog>
</template>

<script>
import AppDialog from "@/components/AppDialog";
import whalesApi from "@/services/api/whales";

const MERGE_FIELDS = [
  { key: "species", label: "Species" },
  { key: "whaleName", label: "Name" },
  { key: "ecotype", label: "Ecotype" },
  { key: "clan", label: "Clan" },
  { key: "pod", label: "Pod" },
  { key: "matriline", label: "Matriline" },
  { key: "mother", label: "Mother" },
  { key: "maternalConfidence", label: "Maternal confidence" },
  { key: "birthYear", label: "Birth year" },
];

export default {
  name: "MergeDialog",
  components: { AppDialog },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    whaleId: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue", "merged"],
  data() {
    return {
      candidates: [],
      survivorId: null,
      duplicateId: null,
      choices: {},
      customValues: {},
      loading: false,
      merging: false,
      error: "",
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    candidateItems() {
      return this.candidates.map((whale) => ({
        title: `${whale.whaleId} \u2014 ${whale.whaleName || "unnamed"} (id ${whale.id})`,
        value: whale.id,
      }));
    },
    survivor() {
      return this.candidates.find((whale) => whale.id === this.survivorId) || null;
    },
    duplicate() {
      return this.candidates.find((whale) => whale.id === this.duplicateId) || null;
    },
    fieldRows() {
      if (!this.survivor || !this.duplicate) return [];
      return MERGE_FIELDS.map((field) => {
        const survivorValue = this.survivor[field.key];
        const duplicateValue = this.duplicate[field.key];
        return {
          key: field.key,
          label: field.label,
          survivorValue,
          duplicateValue,
          conflict: !this.valuesEqual(survivorValue, duplicateValue),
        };
      });
    },
    fieldOverrides() {
      const overrides = {};
      this.fieldRows.forEach((row) => {
        const choice = this.choices[row.key] || "survivor";
        let value;
        if (choice === "duplicate") {
          value = row.duplicateValue;
        } else if (choice === "custom") {
          value = this.customValues[row.key];
        } else {
          return;
        }
        if (this.valuesEqual(value, row.survivorValue)) return;
        if (row.key === "birthYear") {
          const parsed = value === "" || value === null || value === undefined ? null : Number(value);
          value = Number.isNaN(parsed) ? null : parsed;
        } else if (value === "") {
          value = null;
        }
        overrides[row.key] = value;
      });
      return overrides;
    },
    canMerge() {
      return (
        !this.merging &&
        this.survivor !== null &&
        this.duplicate !== null &&
        this.survivorId !== this.duplicateId
      );
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.load();
      }
    },
    survivorId() {
      this.resetChoices();
    },
    duplicateId() {
      this.resetChoices();
    },
  },
  methods: {
    async load() {
      this.error = "";
      this.loading = true;
      const result = await whalesApi.getDuplicates(this.whaleId);
      this.candidates = result.data || [];
      this.survivorId = this.candidates[0] ? this.candidates[0].id : null;
      this.duplicateId = this.candidates[1] ? this.candidates[1].id : null;
      this.resetChoices();
      this.loading = false;
    },
    resetChoices() {
      const choices = {};
      const customs = {};
      MERGE_FIELDS.forEach((field) => {
        choices[field.key] = "survivor";
        customs[field.key] = "";
      });
      this.choices = choices;
      this.customValues = customs;
    },
    valuesEqual(a, b) {
      return String(a ?? "") === String(b ?? "");
    },
    displayValue(value) {
      if (value === null || value === undefined || value === "") return "\u2014";
      return String(value);
    },
    async doMerge() {
      if (!this.canMerge) return;
      this.merging = true;
      this.error = "";
      try {
        await whalesApi.merge({
          survivorId: this.survivorId,
          duplicateId: this.duplicateId,
          fieldOverrides: this.fieldOverrides,
        });
        this.$emit("merged");
        this.dialog = false;
      } catch (e) {
        this.error = "Merge failed. Please try again.";
      }
      this.merging = false;
    },
  },
};
</script>

<style scoped>
.merge-field-label {
  font-weight: 500;
  white-space: nowrap;
  width: 1%;
}

.merge-choices :deep(.v-selection-control) {
  margin-right: 12px;
}
</style>
